import { branding, clinicTenantId } from './config';

export interface ClinicBranding {
  displayName: string | null;
  tagline: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  hasLogo: boolean;
}

export interface TenantMe {
  tenantId: string;
  name: string;
  plan: string;
  specialty: string;
  modules: Record<string, boolean>;
  products?: Record<string, boolean>;
  packCode?: string;
  features?: Record<string, boolean>;
  branding?: ClinicBranding;
}

const fallbackBranding: ClinicBranding = {
  displayName: branding.name,
  tagline: branding.tagline,
  primaryColor: branding.primaryColor,
  secondaryColor: branding.secondaryColor,
  hasLogo: false,
};

/** Público: landpage. Lo sirve vyecom-admin (nginx /api/tenants/). */
export async function loadClinicBranding(): Promise<ClinicBranding> {
  try {
    const res = await fetch(`/api/tenants/${encodeURIComponent(clinicTenantId())}/branding`);
    if (!res.ok) return fallbackBranding;
    const row = (await res.json()) as ClinicBranding;
    return {
      displayName: row.displayName || branding.name,
      tagline: row.tagline || branding.tagline,
      primaryColor: row.primaryColor || branding.primaryColor,
      secondaryColor: row.secondaryColor || branding.secondaryColor,
      hasLogo: !!row.hasLogo,
    };
  } catch {
    return fallbackBranding;
  }
}

export function applyBranding(row: ClinicBranding): void {
  if (row.primaryColor) {
    document.documentElement.style.setProperty('--primary-color', row.primaryColor);
  }
  if (row.secondaryColor) {
    document.documentElement.style.setProperty('--secondary-color', row.secondaryColor);
  }
}

export function clinicName(row?: ClinicBranding | TenantMe | null): string {
  if (!row) return branding.name;
  if ('displayName' in row && row.displayName) return row.displayName;
  if ('branding' in row && row.branding?.displayName) return row.branding.displayName;
  if ('name' in row && row.name) return row.name;
  return branding.name;
}

export function logoUrl(hasLogo: boolean): string {
  return hasLogo
    ? `/api/tenants/${encodeURIComponent(clinicTenantId())}/branding/logo`
    : '/logo.svg';
}

export interface ClinicAssignment {
  tenantId: string;
  products: Record<string, boolean>;
  modules: Record<string, boolean>;
  packCode?: string;
  features?: Record<string, boolean>;
}

export async function loadClinicAssignment(): Promise<ClinicAssignment | null> {
  try {
    const res = await fetch(`/api/tenants/${encodeURIComponent(clinicTenantId())}/assignment`);
    if (!res.ok) return null;
    return (await res.json()) as ClinicAssignment;
  } catch {
    return null;
  }
}

export function featureOn(assignment: ClinicAssignment | null, key: string, fallback = false): boolean {
  if (assignment?.features && Object.keys(assignment.features).length > 0) {
    return assignment.features[key] === true;
  }
  return fallback;
}

/** Producto o módulo encendido: JWT /me, o catálogo público si el token no trae organization. */
export function productOn(
  me: TenantMe | null,
  assignment: { products?: Record<string, boolean>; modules?: Record<string, boolean> } | null,
  key: string
): boolean {
  if (me?.modules?.[key] === true || me?.products?.[key] === true) return true;
  if (assignment?.modules?.[key] === true || assignment?.products?.[key] === true) return true;
  return false;
}

/** El contrato lo sirve vyecom-admin vía nginx /api/tenants/. */
export async function tenantsMe(): Promise<TenantMe | null> {
  try {
    const res = await fetch('/api/tenants/me', {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    return (await res.json()) as TenantMe;
  } catch {
    return null;
  }
}

export function authHeaders(): HeadersInit {
  const token =
    window.__VYEDOCS_ACCESS_TOKEN__ ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('vyedocs_access_token') : null);
  return token ? { Authorization: `Bearer ${token}` } : {};
}
